import React, { useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { DocumentTextIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

const FileViewModal = ({ open, onClose, file }) => {
    const viewerRef = useRef(null);
    const closeBtnRef = useRef(null);

    useEffect(() => {
        if (open && closeBtnRef.current) {
            closeBtnRef.current.focus();
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            // Bloquear print screen (F12, PrintScreen, Ctrl+P, etc.)
            const blockPrint = (e) => {
                if (
                    e.key === 'PrintScreen' ||
                    (e.ctrlKey && e.key === 'p') ||
                    (e.metaKey && e.key === 'p') ||
                    e.key === 'F12'
                ) {
                    e.preventDefault();
                    alert('Ação bloqueada por segurança.');
                }
            };
            window.addEventListener('keydown', blockPrint);
            return () => window.removeEventListener('keydown', blockPrint);
        }
    }, [open]);

    // Bloquear menu de contexto (botão direito)
    useEffect(() => {
        const blockContext = (e) => {
            e.preventDefault();
        };
        if (open && viewerRef.current) {
            viewerRef.current.addEventListener('contextmenu', blockContext);
            return () => viewerRef.current.removeEventListener('contextmenu', blockContext);
        }
    }, [open]);

    if (!file) return null;
    const isPDF = file.file_name?.toLowerCase().endsWith('.pdf');
    const src = isPDF ? `data:application/pdf;base64,${file.file_path}` : `data:image/*;base64,${file.file_path}`;

    return (
        <Transition.Root show={open} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-80 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 select-none" ref={viewerRef} style={{ userSelect: 'none' }}>
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="bg-white rounded-xl shadow-xl  w-[90%] p-0 flex flex-col items-center relative overflow-hidden ">
                            {/* Header bonito e moderno */}
                            <div className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 px-8 py-5 flex items-center gap-4 border-b shadow-md">
                                <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-white bg-opacity-20">
                                    {isPDF ? <DocumentTextIcon className="h-8 w-8 text-white" /> : <PhotoIcon className="h-8 w-8 text-white" />}
                                </span>
                                <div className="flex flex-col flex-1 min-w-0">
                                    <span className="text-lg font-bold text-white truncate" title={file.title}>{file.title}</span>
                                    <span className="text-xs text-blue-100 truncate">{file.file_name}</span>
                                </div>
                                <button ref={closeBtnRef} className="text-white hover:text-red-200 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded p-1 ml-2" onClick={onClose} title="Fechar visualização">✕</button>
                            </div>
                            {/* Área de visualização com overlay de proteção */}
                            <div className="w-full h-[70vh] flex items-center justify-center overflow-hidden rounded-lg bg-gray-100 relative" style={{ userSelect: 'none' }}>
                                {/* Overlay de linhas d’água */}
                                <div className="absolute inset-0 pointer-events-none select-none opacity-20 z-10" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #3b82f6 0 2px, transparent 2px 40px)', mixBlendMode: 'multiply' }} />
                                {/* Overlay de texto d'água */}
                                <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none select-none" style={{ opacity: 0.25 }}>
                                    <span style={{
                                        fontSize: '3vw',
                                        color: '#1e40af',
                                        fontWeight: 'bold',
                                        transform: 'rotate(-25deg)',
                                        whiteSpace: 'nowrap',
                                        textShadow: '0 2px 8px #fff',
                                        letterSpacing: 2
                                    }}>
                                        Visualização protegida - Confidencial
                                    </span>
                                </div>
                                {isPDF ? (
                                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                        <div
                                            className="w-full h-full relative z-20"
                                            style={{ userSelect: 'none', pointerEvents: 'auto' }}
                                            onContextMenu={e => e.preventDefault()}
                                        >
                                            <Viewer
                                                fileUrl={`data:application/pdf;base64,${file.file_path}`}
                                                defaultScale={window.innerWidth <= 768 ? 0.6 : 2.0}
                                                plugins={[]}
                                                renderToolbar={() => null}
                                                renderLoader={() => <div className="text-center text-gray-500">Carregando PDF...</div>}
                                            />
                                        </div>
                                    </Worker>
                                ) : (
                                    <img
                                        src={src}
                                        alt={file.title}
                                        className="max-h-full max-w-full object-contain pointer-events-none select-none relative z-20"
                                        draggable={false}
                                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                                    />
                                )}
                            </div>
                            <div className="mt-2 mb-3 w-[100%] text-sm text-center text-red-500 font-semibold bg-red-50 px-4 py-2 rounded shadow-inner">Visualização protegida. Download, print e cópia estão bloqueados.</div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default FileViewModal; 